import React, { useEffect, useMemo, useState } from "react";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import { Chip, Tooltip, IconButton, ToggleButton, ToggleButtonGroup } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ReplayIcon from "@mui/icons-material/Replay";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import { useNavigate } from "react-router-dom";
import DashboardNavbar from "../../../../../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../../../../../examples/LayoutContainers/DashboardLayout";
import Footer from "../../../../../../examples/Footer";
import MDButton from "../../../../../../components/MDButton";
import { Tooltip as MuiTooltip } from "@mui/material";
import * as XLSX from "xlsx";
import { appConfirm } from "components/AppDialog";

// e-Invoice (EDM) status saved on the customer card. Only valid for the tax ID it
// was checked for; "unchecked" when never looked up (e.g. no valid VKN/TCKN).
const getEInvoiceStatus = (customer) => {
    const status = customer?.eInvoice;
    const taxID = String(customer?.taxID ?? "").trim();

    if (!status?.checkedAt || status.checkedTaxID !== taxID) return "unchecked";

    if (status.isRegistered) return "registered";

    // "Not registered" is only a fact when it came from the production EDM; the
    // EDM test service knows just a few companies (and older rows have no
    // environment recorded), so those stay unconfirmed.
    return status.environment === "production" ? "not-registered" : "unconfirmed";
};

const E_INVOICE_LABELS = {
    registered: "e-Invoice",
    "not-registered": "e-Archive",
    unconfirmed: "Not found",
    unchecked: "",
};

function CustomerTable() {
    const [data, setData] = useState([]);
    const [notification, setNotification] = useState({ value: false, text: "" });
    const [statusFilter, setStatusFilter] = useState("active");
    const navigate = useNavigate();

    // ✅ tab değiştikçe doğru dataset çek (active: sadece aktifler, all/deactive: hepsi + frontend filtre)
    useEffect(() => {
        let alive = true;

        (async () => {
            try {
                const params = statusFilter === "active" ? {} : { includeDeactive: true };
                const res = await CrudService.getCustomers(params);

                const normalized = (res?.data || []).map((item) => ({
                    id: item.id,
                    ...item.attributes,
                }));

                if (alive) setData(normalized);
            } catch (err) {
                if (alive) setNotification({ value: true, text: "Failed to load customers." });
            }
        })();

        return () => {
            alive = false;
        };
    }, [statusFilter]);

    const filteredData = useMemo(() => {
        switch (statusFilter) {
            case "deactive":
                return data.filter((c) => c.isActive === false);
            case "all":
                return data;
            case "active":
            default:
                return data.filter((c) => c.isActive !== false);
        }
    }, [data, statusFilter]);

    // ✅ Soft Deactivate (isActive=false)
    const handleDeactivate = async (id) => {
        if (!await appConfirm("Are you sure you want to deactivate this account?")) return;
        try {
            await CrudService.updateCustomer({ data: { isActive: false } }, id);
            setData((prev) => prev.map((c) => (c.id === id ? { ...c, isActive: false } : c)));
        } catch (err) {
            setNotification({ value: true, text: "Failed to deactivate." });
        }
    };

    // ✅ Reactivate (isActive=true)
    const handleReactivate = async (id) => {
        try {
            await CrudService.updateCustomer({ data: { isActive: true, deactivationReason: "" } }, id);
            setData((prev) => prev.map((c) => (c.id === id ? { ...c, isActive: true } : c)));
        } catch (err) {
            setNotification({ value: true, text: "Failed to reactivate." });
        }
    };

    // ✅ Hard Delete (tamamen DB’den sil)
    const handleHardDelete = async (id) => {
        if (!await appConfirm("This will permanently delete the account. Continue?")) return;
        try {
            await CrudService.deleteCustomer(id);
            setData((prev) => prev.filter((c) => c.id !== id));
        } catch (err) {
            setNotification({ value: true, text: "Failed to delete permanently." });
        }
    };

    const rows = useMemo(
        () =>
            filteredData.map((c) => ({
                id: c.id,
                customerNo: c.customerNo,
                name: (
                    <MuiTooltip title={c.name || ""} placement="top" arrow>
                        <span>{c.name?.length > 40 ? `${c.name.slice(0, 37)}...` : c.name || ""}</span>
                    </MuiTooltip>
                ),
                salesRep: Array.isArray(c.salesRep)
                    ? c.salesRep
                        .map((r) => (typeof r === "object" && r !== null ? r.name || r.label : null))
                        .filter(Boolean)
                        .join(", ")
                    : typeof c.salesRep === "object" && c.salesRep !== null
                        ? c.salesRep.name
                        : "-",
                customerCountry: c.customerCountry,
                taxDetails: `${c.taxOffice || ""} - ${c.taxID || ""}`,
                eInvoice: E_INVOICE_LABELS[getEInvoiceStatus(c)],
                eInvoiceStatus: getEInvoiceStatus(c),
                eInvoiceTitle: c.eInvoice?.title || "",
                paymentTerms: c.paymentTerms,
                customerStatus: c.customerStatus,
                serviceType: Array.isArray(c.serviceType) ? c.serviceType.join(", ") : "",
                actions: (
                    <MDBox display="flex" alignItems="center" gap="6px">
                        <Tooltip title="Edit Account">
                            <IconButton onClick={() => navigate(`/customer/create-customer/details?id=${c.id}`)}>
                                <EditIcon sx={{ color: "blue" }} />
                            </IconButton>
                        </Tooltip>

                        {c.isActive !== false ? (
                            // ✅ Active: Deactivate
                            <Tooltip title="Deactivate Account">
                                <IconButton onClick={() => handleDeactivate(c.id)}>
                                    <DeleteIcon sx={{ color: "red" }} />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            // ✅ Deactive: Reactivate + Hard Delete
                            <>
                                <Tooltip title="Reactivate Account">
                                    <IconButton onClick={() => handleReactivate(c.id)}>
                                        <ReplayIcon sx={{ color: "green" }} />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Delete Account">
                                    <IconButton onClick={() => handleHardDelete(c.id)}>
                                        <DeleteIcon sx={{ color: "red" }} />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </MDBox>
                ),
            })),
        [filteredData, navigate]
    );

    // ✅ DataTable loop fix: columns & tableConfig memo
    const columns = useMemo(
        () => [
            {
                Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Account ID</span>,
                accessor: "customerNo",
                Cell: ({ row }) => (
                    <span
                        style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }}
                        onClick={() => navigate(`/customer/create-customer/details?id=${row.original.id}`)}
                    >
            {row.original.customerNo}
          </span>
                ),
            },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Account</span>, accessor: "name" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Country</span>, accessor: "customerCountry" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Tax Office-ID</span>, accessor: "taxDetails" },
            {
                Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>e-Invoice</span>,
                accessor: "eInvoice",
                Cell: ({ row }) => {
                    const { eInvoiceStatus, eInvoiceTitle } = row.original;

                    if (eInvoiceStatus === "unchecked") return <span style={{ opacity: 0.5 }}>-</span>;

                    return (
                        <Tooltip
                            title={
                                eInvoiceStatus === "registered"
                                    ? `e-Invoice user${eInvoiceTitle ? ` - ${eInvoiceTitle}` : ""}`
                                    : eInvoiceStatus === "unconfirmed"
                                        ? "Not found in the EDM test list - not confirmed. Check it against the production EDM."
                                        : "Not an e-Invoice user (e-Archive)"
                            }
                            arrow
                        >
                            <Chip
                                size="small"
                                color={
                                    eInvoiceStatus === "registered"
                                        ? "success"
                                        : eInvoiceStatus === "unconfirmed"
                                            ? "warning"
                                            : "default"
                                }
                                variant={eInvoiceStatus === "unconfirmed" ? "outlined" : "filled"}
                                label={E_INVOICE_LABELS[eInvoiceStatus]}
                            />
                        </Tooltip>
                    );
                },
            },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Payment Terms</span>, accessor: "paymentTerms" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Sales Rep</span>, accessor: "salesRep" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Status</span>, accessor: "customerStatus" },
            { Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Service Type</span>, accessor: "serviceType" },
            {
                Header: () => <span style={{ fontWeight: "bold", fontSize: "14px" }}>Actions</span>,
                accessor: "actions",
                disableSortBy: true,
            },
        ],
        [navigate]
    );

    const tableConfig = useMemo(() => ({ columns, rows }), [columns, rows]);

    const handleExportExcel = () => {
        const rowsForExcel = filteredData.map((c) => {
            const salesRepStr = Array.isArray(c.salesRep)
                ? c.salesRep
                    .map((r) => (typeof r === "object" && r ? r.name || r.label : r))
                    .filter(Boolean)
                    .join(", ")
                : typeof c.salesRep === "object" && c.salesRep
                    ? c.salesRep.name
                    : c.salesRep || "-";

            return {
                "Account ID": c.customerNo ?? "",
                Customer: c.name ?? "",
                Country: c.customerCountry ?? "",
                "Tax Office": c.taxOffice ?? "",
                "Tax ID": c.taxID ?? "",
                "e-Invoice":
                    getEInvoiceStatus(c) === "unconfirmed"
                        ? "Not found (unconfirmed)"
                        : E_INVOICE_LABELS[getEInvoiceStatus(c)] || "Unchecked",
                "Payment Terms": c.paymentTerms ?? "",
                "Credit Limit": c.creditLimit ?? "",
                "Sales Rep(s)": salesRepStr,
                Status: c.customerStatus ?? "",
                "Service Type": Array.isArray(c.serviceType) ? c.serviceType.join(", ") : c.serviceType ?? "",
                Active: c.isActive !== false ? "Active" : "Deactive",
            };
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(rowsForExcel);

        const headers = Object.keys(rowsForExcel[0] || {});
        const colWidths = headers.map((key) => ({
            wch: Math.max(key.length, ...rowsForExcel.map((r) => String(r[key] ?? "").length), 10),
        }));
        ws["!cols"] = colWidths;

        XLSX.utils.book_append_sheet(wb, ws, "Customers");
        const fileName = `customers_${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "")}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />

            {notification.value && (
                <MDAlert color="info" my={2}>
                    <MDTypography color="white">{notification.text}</MDTypography>
                </MDAlert>
            )}

            <MDBox pt={6} pb={3}>
                <MDBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
                    <MDTypography variant="h5">ACCOUNT LIST</MDTypography>

                    <MDBox display="flex" gap={1}>
                        <MDButton variant="gradient" color="info" size="small" onClick={handleExportExcel}>
                            DOWNLOAD
                        </MDButton>

                        <MDButton
                            variant="gradient"
                            color="dark"
                            size="small"
                            onClick={() => navigate("/customer/create-customer/details")}
                        >
                            + Create Account
                        </MDButton>
                    </MDBox>
                </MDBox>

                <MDBox mb={3} display="flex" justifyContent="flex-end">
                    <ToggleButtonGroup
                        value={statusFilter}
                        exclusive
                        onChange={(_, val) => {
                            if (!val) return;
                            if (val === statusFilter) return;
                            setStatusFilter(val);
                        }}
                        size="small"
                    >
                        <ToggleButton value="active">Active</ToggleButton>
                        <ToggleButton value="deactive">Deactive</ToggleButton>
                        <ToggleButton value="all">All</ToggleButton>
                    </ToggleButtonGroup>
                </MDBox>

                <DataTable table={tableConfig} canSearch />
            </MDBox>

            <Footer />
        </DashboardLayout>
    );
}

export default CustomerTable;
